/* Is this password one an attacker already has? Asked of Have I Been Pwned's range API, by
   k-anonymity: the password is hashed here, and only the first five of the hash's forty
   characters leave the device. The service answers with every breached hash that starts that
   way, several hundred of them, and the comparison happens here. Neither the password nor its
   hash is ever sent, and the service cannot tell which of the hundreds was meant.

   Supabase has this check on the server, on its paid plans. On the free plan it is done in
   the browser, which protects the honest user from a bad choice and nobody from themselves:
   someone who goes round the app to set a breached password on their own account can. */

const RANGE_API = "https://api.pwnedpasswords.com/range/";
/** The origin the browser talks to, for the Content Security Policy. */
export const PWNED_ORIGIN = new URL(RANGE_API).origin;

const TIMEOUT_MS = 4000;

export type Fetch = (url: string, init?: RequestInit) => Promise<Response>;

/** How many times a password shows up in known breaches; 0 for never; null when the service
    could not be asked. */
export type PwnedCheck = (password: string) => Promise<number | null>;

async function sha1Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-1", new TextEncoder().encode(text));
  return [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase();
}

/** The count beside our hash's suffix in the service's answer: lines of `SUFFIX:COUNT`. */
export function countIn(answer: string, suffix: string): number {
  for (const line of answer.split("\n")) {
    const [theirs, count] = line.trim().split(":");
    if (theirs?.toUpperCase() === suffix) return Number.parseInt(count ?? "0", 10) || 0;
  }
  return 0;
}

export function createPwnedCheck(fetcher: Fetch = (url, init) => fetch(url, init)): PwnedCheck {
  return async (password) => {
    try {
      const hash = await sha1Hex(password);
      const response = await fetcher(RANGE_API + hash.slice(0, 5), {
        // Padded with made-up entries of count 0, so that the size of the answer says nothing
        // about the prefix that was asked.
        headers: { "Add-Padding": "true" },
        signal: AbortSignal.timeout(TIMEOUT_MS),
        // Nothing of ours goes with it: no cookie, no referrer.
        credentials: "omit",
        referrerPolicy: "no-referrer",
      });
      if (!response.ok) return null;
      return countIn(await response.text(), hash.slice(5));
    } catch {
      // Offline, blocked, slow. Whoever asked decides what not knowing means.
      return null;
    }
  };
}

/** What the forms say. Plain about why: a rule with no reason is a rule people work around. */
export const pwnedMessage = (count: number): string =>
  `This password has been found in known data breaches (${count.toLocaleString("en")} time${
    count === 1 ? "" : "s"
  }), so it is among the first an attacker would try. Choose another.`;
